import React, { useState } from "react";

import { IconButton, Image, MenuDrawer } from "@global";
import { CartBubble, Container } from "@util/standard";
import { determineBrand } from "@util/theme.js";
import { navigate } from "gatsby";
import MobileMenu from "./mobileMenu";
import { MobileContainer, MobileExternalLink } from "./shared.styles";

interface Props {
  logo: Queries.Maybe<Queries.SanityImageWithMeta> | undefined;
  reverseLogo: Queries.Maybe<Queries.SanityImageWithMeta> | undefined;
  hasScrolled: boolean;
  isDark?: boolean;
  menuItemLinks: Queries.Maybe<
    readonly Queries.Maybe<Queries.SanityLinkOrMegaMenuLinks>[]
  >;
  externalLinks: Queries.Maybe<readonly Queries.Maybe<Queries.SanityLink>[]>;
  drawer: {
    openCart: () => void;
    openAccount: () => void;
  };
  cartTotal: number;
}

const MobileHeader = ({
  logo,
  reverseLogo,
  hasScrolled,
  isDark,
  menuItemLinks,
  externalLinks,
  drawer,
  cartTotal,
}: Props) => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const size = {
    drawerLogo: 100,
    mobileLogo: 100,
    desktopLogo: 120,
  };

  const ibanText = () => {
    const theme = determineBrand();
    if (theme === "barkers-nz") return "Barker's of Geraldine";
    if (theme === "barkers-aus") return "Barker's of New-Zealand";
    if (theme === "everyday") return "Everyday+ by Barker's";
    if (theme === "professional") return "Barker's Professional";
    if (theme === "anathoth") return "Anathoth Farm";

    return "Barker's of Geraldine";
  };

  const media = {
    hamburger: () => {
      if (!hasScrolled && !isDark) {
        return "hamburger";
      }
      return "hamburgerDark";
    },
    cart: () => {
      if (!hasScrolled && !isDark) {
        return "cart";
      }
      return "cartDark";
    },
    account: () => {
      if (!hasScrolled && !isDark) {
        return "account";
      }
      return "accountDark";
    },
    logo: () => {
      if (reverseLogo && (hasScrolled || isDark)) {
        return reverseLogo.asset?.url;
      }
      if (logo) {
        return logo.asset?.url;
      }
      return null;
    },
  };

  return (
    <MobileContainer>
      <Container
        alignItems="center"
        width={`calc(50% - (${size.desktopLogo}px / 2))`}
        mobileWidth={`calc(50% - (${size.mobileLogo}px / 2))`}
      >
        <IconButton
          height="14px"
          width="23px"
          icon={media.hamburger()}
          onClick={() => setDrawerVisible(true)}
        />
      </Container>
      <Container
        width={`${size.desktopLogo}px`}
        mobileWidth={`${size.mobileLogo}px`}
      >
        <Image
          staticImage={media.logo()}
          width="100%"
          height="auto"
          altText={logo?.altText as string}
          onClick={() => navigate("/")}
        />
      </Container>
      <Container
        alignItems="center"
        justifyContent="flex-end"
        width={`calc(50% - (${size.desktopLogo}px / 2))`}
        mobileWidth={`calc(50% - (${size.mobileLogo}px / 2))`}
      >
        <Container position="relative">
          <IconButton
            onClick={drawer.openCart}
            width="18px"
            height="17px"
            icon={media.cart()}
          />
          {cartTotal > 0 && (
            <CartBubble position="absolute">
              <span>{cartTotal}</span>
            </CartBubble>
          )}
        </Container>
        <IconButton
          onClick={drawer.openAccount}
          width="16px"
          height="16px"
          margin="0 0 0 13px"
          icon={media.account()}
        />
      </Container>

      <MenuDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        placement="left"
      >
        {/* Mobile Drawer Header */}
        <Container alignItems="center" padding="20px 30px 0">
          <Container width={`calc(50% - (${size.drawerLogo}px / 2))`}>
            <IconButton
              width="17px"
              height="17px"
              icon="close"
              onClick={() => setDrawerVisible(false)}
            />
          </Container>
          <Container width={`${size.drawerLogo}px`}>
            <Image
              staticImage={
                reverseLogo ? reverseLogo.asset?.url : logo?.asset?.url
              }
              width="100%"
              height="50px"
              altText={logo?.altText as string}
              onClick={() => navigate("/")}
            />
          </Container>
          <Container
            width={`calc(50% - (${size.drawerLogo}px / 2))`}
            justifyContent="flex-end"
          ></Container>
        </Container>

        {/* Mobile Drawer Menu */}
        <MobileMenu
          handleDrawerClose={() => setDrawerVisible(false)}
          menuItems={menuItemLinks}
        />

        {/* Mobile Drawer Footer Menu */}
        <div>
          <MobileExternalLink linktext={ibanText()} />
          {externalLinks?.map(externalLink => {
            if (!externalLink) return null;

            return (
              <MobileExternalLink key={externalLink._key} {...externalLink} />
            );
          })}
        </div>
      </MenuDrawer>
    </MobileContainer>
  );
};

export default MobileHeader;
